import { useState } from "react";

import { t } from "i18next";
import styled from "styled-components";

import type { UserCourseLibraryExercise, UserCourseLibraryExercises } from "api/schemas/UserCourse";
import { CloseIcon } from "assets";
import { PrimaryButton } from "shared/atoms/Button";
import { IconButton } from "shared/atoms/IconButton";
import { Notification } from "shared/atoms/Notification";

import { ExerciseLibraryList } from "./ExerciseLibraryList";
import { ExercisePreviewPopup } from "./ExercisePreviewPopup";
import { OuterMargin, SmallPrimaryButton, TopBar, TopLeft, TopRight } from "./SharedStyledComponents";

export const ExercisePicker = ({
  onClose,
  onAddExercises,
  initialSelectedIds = [],
}: {
  onAddExercises: (exercises: UserCourseLibraryExercises) => void;
  onClose: () => void;
  initialSelectedIds?: number[];
}) => {
  const [selectedExercises, setSelectedExercises] = useState<UserCourseLibraryExercises>([]);
  const [previewExercise, setPreviewExercise] = useState<UserCourseLibraryExercise>();
  const [error, setError] = useState<string | null>(null);

  return (
    <>
      <Root>
        <HeaderRow>
          <OuterMargin>
            <TopBar style={{ padding: "0px 32px" }}>
              <TopLeft>
                <Title>{t("usercourse.add_exercises")}</Title>
              </TopLeft>
              <TopRight>
                <SmallPrimaryButton
                  onClick={() => {
                    onAddExercises(selectedExercises);
                  }}
                >
                  {t("buttons.add")}
                </SmallPrimaryButton>
                <IconButton
                  label=""
                  onClick={() => {
                    onClose();
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </TopRight>
            </TopBar>
          </OuterMargin>
        </HeaderRow>
        <OuterMargin>
          <PickerWrapper>
            <ExerciseLibraryList
              multiSelect
              onSelectedExercisesChange={(exercises: UserCourseLibraryExercises) => {
                setSelectedExercises(exercises);
              }}
              onMoreInfoClick={item => {
                setPreviewExercise(item);
              }}
              initialSelectedIds={initialSelectedIds}
              setError={setError}
              selectedExercises={selectedExercises}
            />
          </PickerWrapper>
        </OuterMargin>
      </Root>
      {previewExercise && (
        <ExercisePreviewPopup
          exercise={previewExercise}
          onAddExercise={() => {
            setSelectedExercises(state => [...state, previewExercise]);
            setPreviewExercise(undefined);
          }}
          showAddButton={!selectedExercises.find(e => e.exercise_id === previewExercise.exercise_id)}
          onClose={() => {
            setPreviewExercise(undefined);
          }}
        />
      )}
      {error && (
        <LoadingContainer>
          <CenterText>
            <Notification variant="danger" style={{ marginBottom: "5px" }}>
              {error}
            </Notification>
            <PrimaryButton onClick={onClose}>{t("buttons.close")}</PrimaryButton>
          </CenterText>
        </LoadingContainer>
      )}
    </>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const CenterText = styled.div`
  text-align: center;
`;

const Title = styled.span`
  font-size: 22px;
  font-weight: 600;
`;

const HeaderRow = styled.div`
  background-color: #fff;
  padding: 16px 0 12px 0;
  margin-bottom: 20px;
`;

const Root = styled.div`
  background-color: #f8f8f8;
`;

const PickerWrapper = styled.div`
  padding: 0 32px;
`;
